import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, BriefcaseBusiness, Eye, Pencil, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { departmentMasterAPI } from '../services/api';
import { getMasterCrudAccess } from '../lib/masterData';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';
import { EmptyState, CardSkeleton } from '../components/common/States';
import { StatCard } from '../components/common/StatCard';

const AdminDesignationsMasterPage = ({ routeBasePrefix = '/admin', dashboardHomePath = '/admin' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [includeInactive, setIncludeInactive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [subDepartmentFilter, setSubDepartmentFilter] = useState('all');
  const access = useMemo(() => getMasterCrudAccess(user), [user]);
  const departmentsPath = `${routeBasePrefix}/master/departments`;
  const subDepartmentsPath = `${routeBasePrefix}/master/subdepartments`;
  const designationsPath = `${routeBasePrefix}/master/designations`;
  const importPath = `${routeBasePrefix}/import/designation_master`;

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      const res = await departmentMasterAPI.list({ include_inactive: includeInactive });
      setRows(res.data || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load designations'));
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const selectedDeptForFilter = useMemo(
    () => rows.find((row) => String(row.id) === String(departmentFilter)),
    [rows, departmentFilter]
  );

  const designationRows = useMemo(
    () =>
      rows.flatMap((dept) =>
        (dept.sub_departments || []).flatMap((sub) =>
          (sub.designations || []).map((designation) => ({
            ...designation,
            department_id: dept.id,
            department_name: dept.name,
            sub_department_id: sub.id,
            sub_department_name: sub.name,
          }))
        )
      ),
    [rows]
  );

  const filteredRows = useMemo(() => {
    return designationRows.filter((row) => {
      if (departmentFilter !== 'all' && String(row.department_id) !== String(departmentFilter)) return false;
      if (subDepartmentFilter !== 'all' && String(row.sub_department_id) !== String(subDepartmentFilter)) return false;
      return true;
    });
  }, [designationRows, departmentFilter, subDepartmentFilter]);

  useEffect(() => {
    const successMessage = location.state?.masterSuccessMessage;
    if (!successMessage) return;
    toast.success(successMessage);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-foreground">Designation Master</h2>
          <p className="text-sm text-muted-foreground">Manage designations with department and sub-department filters.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={() => navigate(importPath)}>Import Designations</Button>
          <Button variant="outline" onClick={() => navigate(departmentsPath)}>Departments</Button>
          <Button variant="outline" onClick={() => navigate(subDepartmentsPath)}>Sub-Departments</Button>
          {access.canCreate ? (
            <Button onClick={() => navigate(`${designationsPath}/new`)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Designation
            </Button>
          ) : null}
          <Button type="button" variant="outline" onClick={() => navigate(dashboardHomePath)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
      </div>

      <StatCard label="Total Designations" value={filteredRows.length} icon={BriefcaseBusiness} tone="primary" />

      <Card>
        <CardHeader><CardTitle>Designation Table</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid md:grid-cols-4 gap-3">
            <div className="flex items-center gap-2">
              <Checkbox id="designation-include-inactive" checked={includeInactive} onCheckedChange={(v) => setIncludeInactive(Boolean(v))} />
              <Label htmlFor="designation-include-inactive">Show Inactive</Label>
            </div>
            <div>
              <Label>Department Filter</Label>
              <Select value={departmentFilter} onValueChange={(value) => { setDepartmentFilter(value); setSubDepartmentFilter('all'); }}>
                <SelectTrigger><SelectValue placeholder="All Departments" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {rows.map((row) => (
                    <SelectItem key={row.id} value={String(row.id)}>{row.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Sub-Department Filter</Label>
              <Select value={subDepartmentFilter} onValueChange={setSubDepartmentFilter}>
                <SelectTrigger><SelectValue placeholder="All Sub-Departments" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sub-Departments</SelectItem>
                  {(selectedDeptForFilter?.sub_departments || []).map((sub) => (
                    <SelectItem key={sub.id} value={String(sub.id)}>{sub.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Department</TableHead>
                <TableHead>Sub-Department</TableHead>
                <TableHead>Designation</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <CardSkeleton rows={5} />
                  </TableCell>
                </TableRow>
              ) : null}
              {!loading && filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <EmptyState
                      icon={BriefcaseBusiness}
                      title="No designations found"
                      description="Adjust the filters or create a designation to get started."
                      {...(access.canCreate ? { actionLabel: 'Add Designation', onAction: () => navigate(`${designationsPath}/new`) } : {})}
                    />
                  </TableCell>
                </TableRow>
              ) : null}
              {filteredRows.map((row) => (
                <TableRow key={row.id} className="card-interactive">
                  <TableCell>{row.department_name}</TableCell>
                  <TableCell>{row.sub_department_name}</TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>
                    <Badge variant={row.is_active ? 'success' : 'secondary'}>{row.is_active ? 'Active' : 'Inactive'}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {access.canView ? (
                        <Button size="icon" variant="outline" title="View" onClick={() => navigate(`${designationsPath}/${row.id}/view`)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      ) : null}
                      {access.canEdit ? (
                        <Button size="icon" variant="outline" title="Edit" onClick={() => navigate(`${designationsPath}/${row.id}/edit`)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      ) : null}
                      {access.canDelete ? (
                        <Button size="icon" variant="outline" title="Delete" onClick={() => navigate(`${designationsPath}/${row.id}/delete`)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      ) : null}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDesignationsMasterPage;
